import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setUnreadCount as publishUnread } from '../lib/unread';
import { describeDegraded } from '../lib/apiClient';
import { noticeCountOf, resetBadge, setNoticeCount } from '../lib/appBadge';
import { useFeatures } from '../lib/features';
import { relative } from '../lib/when';
import { Icon, NoteGlyph } from './todos/icons';
import { dueSaid } from './todos/ReminderAlerts';
import AnnouncementFeed from './announcements/AnnouncementFeed';
import './NotificationsBell.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * What is waiting for you, in the corner.
 *
 * This has never shown anything. The two endpoints behind it were hard-coded
 * stubs in server.js returning an empty list and a zero count, so the bell was
 * correct and useless at the same time. They are computed from the data now -
 * see routes/notifications.js for why derived rather than stored.
 *
 * Three things changed here besides pointing at a working endpoint:
 *
 *   The token is a prop. It read localStorage('token') directly, which is the
 *   staff key. A contractor's session is not there, so the contractor portal
 *   could not have used this component even once the endpoint worked.
 *
 *   Each item goes somewhere. A notice about five tickets needing assignment
 *   that cannot be clicked is a smaller version of the problem it is reporting.
 *
 *   The styles moved out of a <style> tag in the JSX. Those rules were
 *   unscoped - `.notification-item`, `.close-btn` - and injected into the page
 *   wherever this happened to render, and `background: white` on the dropdown
 *   meant it stayed white in dark mode with near-white text on it.
 */
/*
 * A TO-DO REMINDER in the list (batch 3). The server adds one per reminder
 * that has fallen due and not been answered: { id: 'todo-reminder-<id>',
 * kind: 'todo_reminder', title, message ('Due today at 15:00'), href
 * ('/todos?item=<todo_id>'), reminder_id, todo_id, due_date, due_time }.
 * Read defensively all the same - the kind in `kind` or `type`, the to-do's
 * fields at the top or under `todo` / `data`, a message and a link made up
 * when missing - because a row that throws takes the whole bell with it.
 *
 * Pressing it marks it read, which on the server ACKNOWLEDGES the reminder,
 * so the card in the corner (ReminderAlerts) goes too; and an answer given
 * on that card refreshes this list ('jarvis:reminders-changed').
 */
const isReminder = (n) => n && (n.kind === 'todo_reminder' || n.type === 'todo_reminder');
const reminderRow = (n, todoPath) => {
  const d = { ...(n.data && typeof n.data === 'object' ? n.data : {}),
              ...(n.todo && typeof n.todo === 'object' ? n.todo : {}) };
  const todoId = n.todo_id ?? d.todo_id ?? d.id ?? null;
  const date = n.due_date ?? d.due_date ?? null;
  const time = n.due_time ?? d.due_time ?? null;
  return {
    key: n.id ?? `todo_reminder-${n.reminder_id ?? d.reminder_id ?? todoId}`,
    title: n.todo_title || d.title || n.title || 'A to-do is due',
    message: n.message || (date || time ? dueSaid(date, time, new Date()) : 'Due now'),
    href: n.href || (todoId !== null && todoId !== undefined
      ? `${todoPath}?item=${encodeURIComponent(todoId)}` : todoPath),
    at: n.created_at || n.fire_at || d.fire_at || null
  };
};

/*
 * SHARING AND GIVING (batch 4): 'todo_assigned' (somebody gave me a to-do),
 * 'todo_comment' (somebody commented on one of mine) and 'todo_shared'
 * (somebody shared a list with me), as { id, kind, title, message, href,
 * created_at, is_read, todo_id?, list_id?, actor_name }. How the server
 * keeps them read is still to be settled, so they are read as defensively
 * as a reminder: the kind in `kind` or `type`, a link made from todo_id or
 * list_id when `href` is missing, and words made from actor_name when
 * `message` is - and pressed, each is marked read with the same per-item
 * call a reminder uses. That call moves a cursor on the server: the event
 * pressed AND every older to-do event go, so they go from here too. "Mark
 * to-dos read" moves the same cursor to now (POST /todos/seen). A link to
 * /todos is the contractor's /todo on the portal, where /todos would only
 * bounce them (and lose the ?item=).
 */
const TODO_KINDS = {
  todo_assigned: { icon: 'person', said: (who) => `${who || 'Somebody'} gave you a to-do` },
  todo_comment: { icon: 'comment', said: (who) => `${who || 'Somebody'} commented on a to-do` },
  todo_shared: { icon: 'share', said: (who) => `${who || 'Somebody'} shared a list with you` },
  // 072: the last thing a to-do of mine was waiting on is done.
  todo_ready: { icon: 'done', said: () => 'A to-do of yours is ready to start' }
};
/* The workload notices (tickets to approve or assign, hours to sign off, a job sent back).
   'recording_ready' (migration 065) is a recorded call whose transcript is
   done: { title, message, href: '/recordings/<id>' }, drawn with a microphone.
   MEETINGS (migration 066) add three, each { title, message, href }:
   'meeting_invited' (somebody put you on an invitation, href the meeting),
   'meeting_starting' (one of yours starts in a quarter of an hour, href the
   meeting's pre-join page) and 'minutes_ready' (the organiser has shared
   the minutes, href the meeting's Minutes tab). Pressed, each is marked
   read by its id like a recording. */
const KIND_ICON = { hours: 'clock', todo_reminder: 'bell', recording_ready: 'mic',
  meeting_invited: 'calendar', meeting_starting: 'today', minutes_ready: 'note',
  // 106: an email waiting to be sorted into to-dos.
  todo_email: 'mail',
  // 125: an announcement not yet opened (or a reminder about one).
  announcement: 'bell' };
const MEETING_KINDS = new Set(['meeting_invited', 'meeting_starting', 'minutes_ready']);
const iconOf = (kind) => KIND_ICON[kind] || 'ticket';

/** "3 h ago", "Yesterday 15:00", "12 Oct" - lib/when, the to-do screens' one voice for when. */
const ago = (iso) => (iso ? relative(iso) : '');
const todoKind = (n) => (n && (TODO_KINDS[n.kind] ? n.kind : TODO_KINDS[n.type] ? n.type : null)) || null;
const todoRow = (n, todoPath) => {
  const kind = todoKind(n);
  const d = n.data && typeof n.data === 'object' ? n.data : {};
  const todoId = n.todo_id ?? d.todo_id ?? null;
  const listId = n.list_id ?? d.list_id ?? null;
  const actor = n.actor_name ?? d.actor_name ?? null;
  let href = typeof n.href === 'string' && n.href ? n.href
    : todoId !== null && todoId !== undefined ? `${todoPath}?item=${encodeURIComponent(todoId)}`
      : listId !== null && listId !== undefined ? `${todoPath}?list=${encodeURIComponent(listId)}` : todoPath;
  if (todoPath !== '/todos' && /^\/todos(?=[?#/]|$)/.test(href)) href = todoPath + href.slice('/todos'.length);
  return {
    key: n.id ?? `${kind}-${todoId ?? listId ?? n.created_at}`,
    kind,
    icon: TODO_KINDS[kind].icon,
    title: n.title || d.title || TODO_KINDS[kind].said(actor),
    message: n.message || (n.title ? TODO_KINDS[kind].said(actor) : ''),
    href,
    at: n.created_at || null
  };
};

const NotificationsBell = ({ token, todoPath = '/todos' }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  /*
   * TWO TABS (125): what is waiting, and the Announcements - the firm's
   * feed, newest first (components/announcements/AnnouncementFeed). An
   * announcement's line in the first opens it in the second. The tab's
   * count is the feed's unread once it has been looked at, and until then
   * the announcement lines the bell already has.
   */
  const [tab, setTab] = useState('waiting');
  const [focusAnn, setFocusAnn] = useState(null);
  const [annUnread, setAnnUnread] = useState(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const features = useFeatures();
  // Announcements switched off for this person (wave 6): no tab - the
  // server answers no lines and an empty feed anyway.
  const annOn = features ? features.features.announcements !== false : true;
  useEffect(() => { if (!annOn && tab === 'announcements') setTab('waiting'); }, [annOn, tab]);

  // A contractor's token is passed in. Staff need nothing here: lib/apiClient
  // patches fetch and attaches the real Entra token. The localStorage('token')
  // fallback this used to carry has not been written by anything since staff
  // sign-in moved to Entra, so it only ever produced `Bearer null`.
  const headers = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/notifications`, {
        headers: headers(), credentials: 'include'
      });
      if (res.status === 401) {
        setNotifications([]);
        setUnreadCount(0);
        // Signed out: a badge left over from the last session sits above an
        // empty chat screen and reads as a bug in the chat screen.
        publishUnread(0);
        resetBadge();
        setLoaded(false);
        return;
      }
      if (!res.ok) throw new Error('Could not read notifications');
      const data = await res.json();
      const list = Array.isArray(data.notifications) ? data.notifications : [];
      setNotifications(list);
      setUnreadCount(Number(data.unread_count) || list.length);
      // The count of MESSAGES, which is a different number from the count of
      // notices above it, and is what the badge on the Chat menu item shows.
      // Published from here rather than fetched again: this response already
      // carries it, and two polls would be two numbers that can disagree on
      // screen.
      publishUnread(data.chat_unread);
      // A failed read answers 200 with an empty list and this header, which is
      // otherwise indistinguishable from having nothing waiting.
      setError(res.headers.get('X-Data-Degraded') ? describeDegraded(res.headers.get('X-Data-Degraded')) : null);
      setLoaded(true);
    } catch (err) {
      setError('Could not read what is waiting');
    }
  }, [headers]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    window.addEventListener('jarvis:reminders-changed', load);
    /*
     * THE COUNT ON THE JARVIS ICON (lib/appBadge) moves with this poll, so it
     * is asked again whenever the answer has probably changed rather than
     * waiting up to a minute: a conversation read or a message arrived on the
     * chat screen (lib/chatLink announces both - folded into one request when
     * several come at once), and the window coming back into view after
     * being minimised or behind something else.
     */
    let soon = null;
    const shortly = () => {
      clearTimeout(soon);
      soon = setTimeout(load, 400);
    };
    let lastLook = Date.now();
    const looked = () => {
      if (document.visibilityState === 'hidden' || Date.now() - lastLook < 5000) return;
      lastLook = Date.now();
      load();
    };
    window.addEventListener('jarvis:chat-read', shortly);
    window.addEventListener('jarvis:chat-activity', shortly);
    window.addEventListener('focus', looked);
    document.addEventListener('visibilitychange', looked);
    return () => {
      clearInterval(interval);
      clearTimeout(soon);
      window.removeEventListener('jarvis:reminders-changed', load);
      window.removeEventListener('jarvis:chat-read', shortly);
      window.removeEventListener('jarvis:chat-activity', shortly);
      window.removeEventListener('focus', looked);
      document.removeEventListener('visibilitychange', looked);
    };
  }, [load]);

  // Gone from the screen (signed out, or a page without the navbar): the icon
  // must not go on saying somebody has messages.
  useEffect(() => () => resetBadge(), []);

  /*
   * What this list adds to the icon's count: its rows less the chat ones,
   * whose messages are counted one by one in chat_unread already - and less
   * anything for a feature switched off for this person. Recomputed when a
   * row is answered from here, so the icon drops at the same moment the bell
   * does. Not before the first answer: an empty list then means "not asked
   * yet", and painting it would clear a badge that is still true.
   */
  useEffect(() => {
    if (!loaded) return;
    const isOn = (key) => (features ? features.features[key] !== false : true);
    setNoticeCount(noticeCountOf(notifications, isOn));
  }, [loaded, notifications, features]);

  /** A to-do event, and the older ones the server's cursor takes with it. */
  const todoGone = (n) => (x) => x === n || (todoKind(x) && Date.parse(x.created_at || '') <= Date.parse(n.created_at || ''));

  /** A reminder (or a to-do event) answered from here: read on the server is acknowledged. */
  const answer = (n, href) => {
    setIsOpen(false);
    const gone = todoKind(n) && n.created_at ? todoGone(n) : (x) => x === n;
    setNotifications((prev) => {
      const left = prev.filter((x) => !gone(x));
      setUnreadCount((c) => Math.max(0, c - (prev.length - left.length)));
      return left;
    });
    if (n.id !== undefined && n.id !== null) {
      fetch(`${API_URL}/notifications/${encodeURIComponent(n.id)}/read`, {
        method: 'POST', headers: headers(), credentials: 'include'
      }).catch(() => {}).then(() => {
        try { window.dispatchEvent(new CustomEvent('jarvis:reminders-changed')); } catch { /* old browser */ }
      });
    }
    if (href) navigate(href);
  };

  // Reload on open: the count polls every minute, but someone pressing the
  // bell wants what is true now, not what was true fifty seconds ago.
  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const away = (e) => { if (!wrapRef.current?.contains(e.target)) setIsOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [isOpen]);

  /** Every to-do event read at once: the server's cursor moved to now. */
  const seeTodos = () => {
    setNotifications((prev) => {
      const left = prev.filter((x) => !todoKind(x));
      setUnreadCount((c) => Math.max(0, c - (prev.length - left.length)));
      return left;
    });
    fetch(`${API_URL}/todos/seen`, { method: 'POST', headers: headers(), credentials: 'include' })
      .catch(() => {});
  };

  const go = (href) => {
    setIsOpen(false);
    if (href) navigate(href);
  };

  /** An announcement's line: open it in the Announcements tab, which marks it read. */
  const openAnnouncement = (n) => {
    setFocusAnn(n.announcement_id || String(n.id || '').replace(/^announcement-/, ''));
    setTab('announcements');
    setNotifications((prev) => {
      const left = prev.filter((x) => x !== n);
      setUnreadCount((c) => Math.max(0, c - (prev.length - left.length)));
      return left;
    });
  };
  const annCount = annUnread !== null ? annUnread : notifications.filter((n) => n.kind === 'announcement').length;
  // Something changed elsewhere (a banner acknowledged): the lines here too.
  useEffect(() => {
    window.addEventListener('jarvis:announcements-changed', load);
    return () => window.removeEventListener('jarvis:announcements-changed', load);
  }, [load]);

  return (
    <div className="nb" ref={wrapRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        className="nav-icon-btn nb-btn"
        title={unreadCount > 0 ? `${unreadCount} waiting` : 'Nothing waiting'}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} waiting` : 'Notifications'}
        aria-expanded={isOpen}
      >
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6"
             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 2.2a4.6 4.6 0 0 0-4.6 4.6c0 3.3-1.2 4.4-1.2 4.4h11.6s-1.2-1.1-1.2-4.4A4.6 4.6 0 0 0 9 2.2Z" />
          <path d="M10.3 14a1.5 1.5 0 0 1-2.6 0" />
        </svg>
        {unreadCount > 0 && <span className="nb-badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="nb-drop" role="dialog" aria-label="Notifications">
          <div className="nb-head">
            <div className="nb-tabs" role="tablist" aria-label="What the bell shows">
              <button type="button" role="tab" id="nb-tab-waiting" aria-selected={tab === 'waiting'}
                      aria-controls="nb-panel" className="nb-tab" onClick={() => setTab('waiting')}>
                Waiting for you
              </button>
              {annOn && (
                <button type="button" role="tab" id="nb-tab-announcements" aria-selected={tab === 'announcements'}
                        aria-controls="nb-panel" className="nb-tab" data-testid="nb-tab-announcements"
                        onClick={() => { setFocusAnn(null); setTab('announcements'); }}>
                  Announcements
                  {annCount > 0 && <span className="nb-tab-count" aria-label={`${annCount} unread`}>{annCount}</span>}
                </button>
              )}
            </div>
            {tab === 'waiting' && notifications.some((n) => todoKind(n)) && (
              <button type="button" className="nb-seen" onClick={seeTodos}>Mark to-dos read</button>
            )}
            <button type="button" onClick={() => setIsOpen(false)} className="nb-x"
                    aria-label="Close notifications">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"
                   strokeLinecap="round" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>

          {/* The list scrolls under the head; the foot fades the last item out
              instead of slicing it through a word. */}
          <div className="nb-body" id="nb-panel" role="tabpanel"
               aria-labelledby={tab === 'waiting' ? 'nb-tab-waiting' : 'nb-tab-announcements'}>
            {tab === 'announcements' && annOn && (
              <AnnouncementFeed token={token} focus={focusAnn} onCount={setAnnUnread} />
            )}
            {tab === 'waiting' && error && <p className="nb-error" role="alert">{error}</p>}

            {tab !== 'waiting' ? null : notifications.length === 0 ? (
              <p className="nb-empty">
                Nothing waiting on you.
              </p>
            ) : (
              <ul className="nb-list">
                {notifications.map((n) => {
                  const r = todoKind(n) ? todoRow(n, todoPath)
                    : isReminder(n) ? { ...reminderRow(n, todoPath), kind: 'todo_reminder' }
                      : { key: n.id, kind: n.kind, title: n.title, message: n.message, href: n.href, at: n.created_at };
                  // A ready recording is read the same way: POST
                  // /notifications/recording-<id>/read, then the page.
                  const mine = todoKind(n) || isReminder(n) || n.kind === 'recording_ready' || MEETING_KINDS.has(n.kind);
                  return (
                    <li key={r.key}>
                      <button type="button" className={`nb-item is-${r.kind}`}
                              data-kind={mine || n.kind === 'announcement' ? r.kind : undefined}
                              onClick={() => (n.kind === 'announcement' ? openAnnouncement(n)
                                : mine ? answer(n, r.href) : go(r.href))}>
                        <span className="nb-ic">
                          {(r.icon || iconOf(r.kind)) === 'mic'
                            ? <NoteGlyph name="mic" size={16} />
                            : <Icon name={r.icon || iconOf(r.kind)} size={16} />}
                        </span>
                        <span className="nb-text">
                          <span className="nb-title">{r.title}</span>
                          {r.message && <span className="nb-msg">{r.message}</span>}
                          {r.at && <time className="nb-when" dateTime={r.at}>{ago(r.at)}</time>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* These are worked out from what is outstanding rather than stored,
                so there is nothing to mark read - an item leaves the list when
                the work behind it is done. Said out loud, because a list with no
                way to dismiss anything otherwise looks broken. */}
            {tab === 'waiting' && notifications.length > 0 && (
              <p className="nb-foot">
                These clear themselves once the work is done.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
